import React, {
  useCallback,
  useEffect,
  useMemo,
  useState,
} from 'react';
import { useDispatch } from 'react-redux';
import PropTypes from 'prop-types';
import { Alert, Drawer, message } from 'antd';
import * as XLSX from 'xlsx';

import StepsContainer from '../../../common/containers/StepsContainer';
import DrawerSubmitFooter from '../../../common/containers/DrawerSubmitFooter';
import SheetUploadPrompt from '../../../common/excel/SheetUploadPrompt';
import SheetHeaderMatcher from '../../../common/excel/SheetHeaderMatcher';
import MassImportSheetSelector from '../../../massImport/MassImportSheetSelector';
import ScheduleOfValuesImportPreview from './ScheduleOfValuesImportPreview';

import {
  importScheduleOfValuesFromExcel,
  previewScheduleOfValuesExcelImport,
} from '../../state/projects.actions';
import toSovImportRequestRows from './sovImportHelpers';
import {
  SOV_IMPORT_GUIDELINES,
  SOV_IMPORT_HEADERS,
  SOV_IMPORT_REQUIRED_KEYS,
  SOV_IMPORT_TEMPLATE_LINK,
} from './sovImportConstants';

const PREVIEW_STEP = 3;

export default function ScheduleOfValuesExcelImportDrawer({
  visible,
  onClose,
  projectId,
}) {
  const dispatch = useDispatch();

  const [currentStep, setCurrentStep] = useState(0);
  const [workbook, setWorkbook] = useState();
  const [selectedSheetName, setSelectedSheetName] = useState();
  const [headerMapping, setHeaderMapping] = useState({});
  const [loading, setLoading] = useState(false);
  const [preview, setPreview] = useState(null);

  useEffect(() => {
    if (visible) return;
    setCurrentStep(0);
    setWorkbook();
    setSelectedSheetName();
    setHeaderMapping({});
    setLoading(false);
    setPreview(null);
  }, [visible]);

  useEffect(() => {
    const [firstSheetName] = workbook?.SheetNames ?? [];
    setSelectedSheetName(firstSheetName);
  }, [workbook]);

  const sheetRows = useMemo(() => {
    const sheet = workbook?.Sheets?.[selectedSheetName];
    return sheet ? XLSX.utils.sheet_to_json(sheet, { defval: '' }) : [];
  }, [workbook, selectedSheetName]);

  const requestRows = useMemo(
    () => toSovImportRequestRows({ sheetRows, headerMapping }),
    [sheetRows, headerMapping],
  );

  // The backend owns every rule, so the preview is the import itself run with nothing written.
  const runPreview = useCallback(async () => {
    setPreview(null);
    if (!requestRows.length) return;
    setLoading(true);
    const result = await dispatch(previewScheduleOfValuesExcelImport({
      projectId,
      rows: requestRows,
    }));
    setPreview(result);
    setLoading(false);
  }, [projectId, requestRows]);

  const onFileChange = useCallback((newWorkbook) => {
    setWorkbook(newWorkbook);
    setCurrentStep(1);
  }, []);

  const onBack = useCallback(() => {
    setPreview(null);
    setCurrentStep((step) => step - 1);
  }, []);

  const onImport = useCallback(async () => {
    setLoading(true);
    const result = await dispatch(importScheduleOfValuesFromExcel({
      projectId,
      rows: requestRows,
    }));
    setLoading(false);

    if (!result) return;

    const count = requestRows.length;
    message.success(`Imported ${count} contract row${count === 1 ? '' : 's'}`);
    onClose();
  }, [projectId, requestRows, onClose]);

  const onNext = useCallback(() => {
    if (currentStep === PREVIEW_STEP) {
      onImport();
      return;
    }
    setCurrentStep((step) => step + 1);
    if (currentStep === PREVIEW_STEP - 1) runPreview();
  }, [currentStep, onImport, runPreview]);

  const canSubmit = useMemo(() => {
    switch (currentStep) {
      case 0:
        return !!workbook;
      case 1:
        return !!selectedSheetName;
      case 2:
        return SOV_IMPORT_REQUIRED_KEYS.every((key) => headerMapping[key]);
      default:
        return !!preview && preview.errorCount === 0;
    }
  }, [currentStep, workbook, selectedSheetName, headerMapping, preview]);

  return (
    <Drawer
      title="Import Contract from Excel"
      visible={visible}
      onClose={onClose}
      width={currentStep === PREVIEW_STEP ? '90vw' : 1000}
    >
      <div style={{ height: 'calc(100% - 55px)', width: '100%', overflowY: 'auto' }}>
        <StepsContainer
          currentStep={currentStep}
          steps={[
            {
              title: 'Upload',
              content: (
                <div>
                  <SheetUploadPrompt
                    onFileChange={onFileChange}
                    showUploadList={false}
                    templateLink={SOV_IMPORT_TEMPLATE_LINK}
                  />
                  <Alert
                    style={{ marginTop: 10 }}
                    type="info"
                    message="Guidelines"
                    description={(
                      <ul style={{ paddingLeft: 20, marginBottom: 0 }}>
                        {SOV_IMPORT_GUIDELINES.map((guideline) => (
                          <li key={guideline}>{guideline}</li>
                        ))}
                      </ul>
                    )}
                  />
                </div>
              ),
            },
            {
              title: 'Select Sheet',
              content: (
                <MassImportSheetSelector
                  workbook={workbook}
                  selectedSheetName={selectedSheetName}
                  onSheetChanged={setSelectedSheetName}
                />
              ),
            },
            {
              title: 'Map Headers',
              content: (
                <SheetHeaderMatcher
                  headerOptions={SOV_IMPORT_HEADERS}
                  selectedSheet={sheetRows}
                  headerMapping={headerMapping}
                  setHeaderMapping={setHeaderMapping}
                />
              ),
            },
            {
              title: 'Preview',
              content: (
                <ScheduleOfValuesImportPreview
                  preview={preview}
                  loading={loading && !preview}
                />
              ),
            },
          ]}
        />
      </div>
      <DrawerSubmitFooter
        onClose={currentStep > 0 ? onBack : null}
        closeTitle="Back"
        onSubmit={onNext}
        submitTitle={currentStep < PREVIEW_STEP ? 'Next' : 'Import'}
        canSubmit={canSubmit}
        loading={loading}
      />
    </Drawer>
  );
}

ScheduleOfValuesExcelImportDrawer.propTypes = {
  visible: PropTypes.bool,
  onClose: PropTypes.func.isRequired,
  projectId: PropTypes.string.isRequired,
};

ScheduleOfValuesExcelImportDrawer.defaultProps = {
  visible: false,
};
